import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { AiOutlineMenu } from "react-icons/ai";
import { TfiClose } from "react-icons/tfi";
import { constants } from "constants";
import { useWindowScroll } from "hooks/useWindowScroll";
import logoImage from "images/optimized/brand/logo.webp";
import { HashLink } from "components/hashLink";
import { Image } from "components/image";

const links = [
  ["about-us", "About"],
  ["our-values", "Values"],
  ["our-services", "Services"],
  ["expertise", "Expertise"],
  ["oss", "OSS"],
  ["apps", "Apps"],
  ["why-choose-us", "Why us"],
  ["contacts", "Contact"],
];

const StyledHeaderShell = styled.div`
  --header-surface: rgba(13, 23, 34, 0.8);
  --header-backdrop: blur(5px) saturate(135%);

  position: fixed;
  top: 14px;
  right: var(--page-gutter);
  left: var(--page-gutter);
  z-index: 20;
  height: ${constants.headerHeight}px;
  transform: ${({ $hidden }) => ($hidden ? "translateY(-130%)" : "none")};
  transition: transform 280ms ease;

  @media (max-width: 500px) {
    top: 10px;
  }
`;

const StyledHeader = styled.header`
  height: 100%;
  border: 1px solid rgba(228, 232, 235, 0.1);
  border-radius: 22px;
  background: var(--header-surface);
  box-shadow: ${({ $menuOpen, $raised }) =>
    $menuOpen
      ? "0 2px 8px rgba(0, 0, 0, 0.18)"
      : $raised
        ? "0 18px 50px rgba(0, 0, 0, 0.32)"
        : "0 10px 30px rgba(0, 0, 0, 0.2)"};
  backdrop-filter: var(--header-backdrop);
  -webkit-backdrop-filter: var(--header-backdrop);
  transition: box-shadow 280ms ease;

  @media (max-width: 500px) {
    border-radius: 18px;
  }
`;

const StyledInner = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 12px 20px;
`;

const StyledLogoLink = styled.a`
  flex: 0 0 auto;
  height: 58px;
  display: flex;
  align-items: center;

  @media (max-width: 700px) {
    height: 50px;
  }
`;

const StyledLogo = styled(Image)`
  width: auto;
  height: 100%;
`;

const StyledNavigation = styled.nav`
  display: flex;
  align-items: center;
  gap: 2px;

  @media (max-width: 1040px) {
    display: none;
  }
`;

const StyledLink = styled(HashLink)`
  display: inline-flex;
  align-items: center;
  min-height: 42px;
  padding: 0 13px;
  border-radius: 999px;
  font-family: Roboto-Regular;
  font-size: 13px;
  letter-spacing: 0.08em;
  color: #dce2e6;
  text-transform: uppercase;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    color: #ffffff;
    background: rgba(57, 137, 166, 0.18);
    outline: none;
  }
`;

const StyledMenuButton = styled.button`
  display: none;
  width: 48px;
  height: 48px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  place-items: center;
  background: rgba(255, 255, 255, 0.07);
  color: #ffffff;
  font-size: 25px;
  cursor: pointer;

  @media (max-width: 1040px) {
    display: grid;
  }
`;

const StyledMobileNavigation = styled.nav`
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  width: min(330px, calc(100vw - 20px));
  display: none;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  padding: 14px;
  border: 1px solid rgba(228, 232, 235, 0.1);
  border-radius: 20px;
  background: var(--header-surface);
  box-shadow: 0 24px 50px rgba(0, 0, 0, 0.34);
  backdrop-filter: var(--header-backdrop);
  -webkit-backdrop-filter: var(--header-backdrop);
  visibility: ${({ $open }) => ($open ? "visible" : "hidden")};
  transform: translateY(${({ $open }) => ($open ? "0" : "-8px")});
  transition:
    transform 180ms ease,
    visibility 180ms;

  & > a {
    justify-content: center;
  }

  @media (max-width: 1040px) {
    display: grid;
  }
`;

export const Header = () => {
  const { y, direction } = useWindowScroll();
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    if (direction === "down") closeMenu();
  }, [direction, closeMenu]);

  useEffect(() => {
    const mobileNavigation = window.matchMedia("(max-width: 1040px)");
    const handleBreakpointChange = ({ matches }) => {
      if (!matches) closeMenu();
    };

    if (!mobileNavigation.matches) closeMenu();
    mobileNavigation.addEventListener("change", handleBreakpointChange);

    return () =>
      mobileNavigation.removeEventListener("change", handleBreakpointChange);
  }, [closeMenu]);

  const hidden = direction === "down" && y > 180 && !menuOpen;

  return (
    <StyledHeaderShell $hidden={hidden}>
      <StyledHeader $menuOpen={menuOpen} $raised={y > 12}>
        <StyledInner>
          <StyledLogoLink href="/" aria-label="Essencebit home">
            <StyledLogo
              src={logoImage}
              alt="Essencebit"
              loading="eager"
              fetchPriority="high"
            />
          </StyledLogoLink>
          <StyledNavigation aria-label="Main navigation">
            {links.map(([id, text]) => (
              <StyledLink
                key={id}
                id={id}
                text={text}
                offset={-constants.headerHeight - 24}
              />
            ))}
          </StyledNavigation>
          <StyledMenuButton
            type="button"
            aria-label={menuOpen ? "Close navigation" : "Open navigation"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <TfiClose /> : <AiOutlineMenu />}
          </StyledMenuButton>
        </StyledInner>
      </StyledHeader>
      <StyledMobileNavigation $open={menuOpen} aria-label="Mobile navigation">
        {links.map(([id, text]) => (
          <StyledLink
            key={id}
            id={id}
            text={text}
            offset={-constants.headerHeight - 20}
            onClick={closeMenu}
          />
        ))}
      </StyledMobileNavigation>
    </StyledHeaderShell>
  );
};
